import {
  useContext,
  useMemo,
  useRef,
  useState,
  type MouseEvent,
  type RefObject,
} from "react";
import { useQuery } from "@tanstack/react-query";
import { Typography, Spin } from "antd";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { journalsQueries, JournalRecordPageShell } from "@/entities/journal";
import type { JournalVersion } from "@/features/journal-version-list";
import { VersionSwitcherDropdown } from "@/features/journal-version-switcher";
import { AbilityContext } from "@/shared/lib/casl";
import { useFlags } from "@/shared/lib/flags";
import { useLocalUser, useCoordinator } from "@/shared/lib/hooks";
import { queryClient } from "@/shared/lib/react-query";
import { useUnifiedAutoResize } from "@/shared/utils";
import { useAppRoutes } from "@shared/lib/react-router";
import { JournalRecordInfo, JournalSigner } from "@shared/ui";
import { getSectionThreeActionItems } from "../../model/actions-map.tsx";
import { useNotifyVersionsForbidden } from "../../model/hooks/use-notify-versions-forbidden";
import { useSectionThirdRecordVersions } from "../../model/hooks/use-section-third-record-versions";
import { useSectionThreeController } from "../../model/hooks/use-section-three-controller";
import { useBuildSignLine } from "../../model/mutate-hooks/use-build-sign-line";
import { isForbiddenError } from "../../model/utils/get-http-error-status";
import { useWithSignedString } from "../../model/utils/with-signed-string";
import { useSectionThreeActions } from "../journal-section-form/model/hooks/use-section-three-actions";
import { useExecutionData } from "../journal-section-info/model/hooks/use-execution-data";
import { useModalInfo } from "../journal-section-info/model/hooks/use-modal-info";
import {
  SectionExecutionData,
  SectionThirdDetails,
  SectionThirdDocuments,
  SectionThirdActions,
} from "../journal-section-info/ui";
import { JournalVersionModal } from "../journals-section-third/journal-version-modal";
import { useGetItemsVersion } from "../journals-section-third/model/use-get-items-version";
import { RejectModal } from "../journals-section-third/ui/reject-modal";
import "./journal-section-info-page.scss";

const { Title } = Typography;

export const JournalSectionInfoPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { itemNoVersionId } = useParams<{ itemNoVersionId: string }>();
  const initialVersionNumber = Number(searchParams.get("versionNumber")) || undefined;
  const { mainJournalId, projectId, objectId, titleId } = useCoordinator();
  const appRoutes = useAppRoutes();
  const isVersionsEnabled = useFlags("ojrFormationFlowEnabledV1") === true;
  const { data: journalData } = useQuery(
    journalsQueries.getJournal({ mainJournalId }),
  );
  const {
    data: itemInfo,
    isFetching,
    isPending,
  } = useModalInfo(itemNoVersionId || "");
  const { data: executionDataResponse } = useExecutionData({
    itemNoVersionId: itemNoVersionId || "",
  });
  const {
    data: versionsData,
    error: versionsError,
    isError: isVersionsError,
  } = useGetItemsVersion(
    journalData?.mainJournal?.section3?.sectionListId,
    itemNoVersionId || undefined,
    isVersionsEnabled,
  );
  const isVersionsForbidden =
    isVersionsEnabled && isVersionsError && isForbiddenError(versionsError);

  useNotifyVersionsForbidden(
    versionsError,
    isVersionsError,
    itemNoVersionId,
    isVersionsEnabled && Boolean(itemNoVersionId),
  );

  const {
    sortedVersionEntries,
    selectedVersionNumber,
    setSelectedVersionNumber,
    selectedEntry,
    maxVersionNumber,
    isViewingHistoricalVersion: isViewingHistoricalVersionRaw,
    displayItemInfo: displayItemInfoRaw,
  } = useSectionThirdRecordVersions(
    itemInfo,
    isVersionsEnabled ? versionsData : undefined,
    itemNoVersionId,
    isVersionsEnabled ? initialVersionNumber : undefined,
    { versionsAccessDenied: isVersionsForbidden },
  );

  const displayItemInfo = isVersionsEnabled ? displayItemInfoRaw : itemInfo;
  const isViewingHistoricalVersion =
    isVersionsEnabled && isViewingHistoricalVersionRaw;

  const versions = useMemo<JournalVersion[]>(
    () =>
      sortedVersionEntries.map((entry) => ({
        versionId: entry.versionInfo?.itemNoVersionId ?? "",
        versionNumber: entry.versionInfo?.versionNumber ?? 0,
        status:
          entry.versionInfo?.isCurrent ||
          entry.versionInfo?.versionNumber === maxVersionNumber
            ? "ACTUAL"
            : "OUTDATED",
        integrationStatus: null,
        recordStatus: entry.versionInfo?.status ?? null,
        versionDate: entry.versionInfo?.updateDate ?? "",
        responsible: null,
      })),
    [sortedVersionEntries, maxVersionNumber],
  );

  const selectedVersionId = useMemo(
    () =>
      sortedVersionEntries.find(
        (e) => e.versionInfo?.versionNumber === selectedVersionNumber,
      )?.versionInfo?.itemNoVersionId ?? "",
    [sortedVersionEntries, selectedVersionNumber],
  );

  const {
    handleSendToSign,
    handleStatusReturn,
    handleReturn,
    handleReject,
  } = useSectionThreeController(
    journalData?.mainJournal?.section3?.sectionListId || "",
    journalData?.mainJournal?.section3?.sectionId || "",
  );

  const { deleteSectionEntry } = useSectionThreeActions(journalData);

  const hasVersion = Boolean(displayItemInfo?.currentVersionId);

  const recordVersionNumber = hasVersion
    ? (selectedEntry?.versionInfo?.versionNumber ??
      sortedVersionEntries[0]?.versionInfo?.versionNumber ??
      (itemInfo as { currentVersionNumber?: number })?.currentVersionNumber ??
      1)
    : undefined;

  const recordVersionDate =
    selectedEntry?.versionInfo?.updateDate ?? itemInfo?.updateDate;

  const signer = displayItemInfo?.signer
    ? `${displayItemInfo.signer.surname} ${displayItemInfo.signer.firstName} ${displayItemInfo.signer.middleName}`
    : "-";

  const localUser = useLocalUser();
  const [signingString, setSigningString] = useState<string>("");
  const [versionsListOpen, setVersionsListOpen] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectComment, setRejectComment] = useState("");
  const [rejectCommentError, setRejectCommentError] = useState("");
  const { mutateAsync: getSigningXml, isPending: isSigningPending } =
    useBuildSignLine();
  const { withSignedString } = useWithSignedString();
  const sectionThreeListId =
    (itemInfo as any)?.section3ListId ||
    (itemInfo as any)?.sectionThreeListId ||
    (journalData as any)?.mainJournal?.section3?.sectionListId ||
    "";
  const isSignerAwaiting =
    !isViewingHistoricalVersion &&
    itemInfo?.status === "AWAITING_SIGNATURE" &&
    itemInfo?.signer?.userId === localUser?.user_id;
  const ability = useContext(AbilityContext);
  const hasEditPermission = ability?.can("edit", "section_3");

  const invalidateItem = () => {
    queryClient.invalidateQueries({
      // @ts-expect-error queryKey invalidate не совпадает с openapi типами
      queryKey: journalsQueries.getSectionThreeItem({ itemNoVersionId })
        .queryKey,
    });
  };

  const handleClose = () => {
    navigate(
      appRoutes.journals.section3(projectId, objectId, mainJournalId, titleId),
    );
  };

  const handleIsForm = (
    status: boolean,
    event?: MouseEvent<HTMLSpanElement>,
  ) => {
    if (event) {
      event.stopPropagation();
    }
    if (!status) {
      navigate(-1);
    }
  };

  const actionItems = getSectionThreeActionItems(
    itemInfo?.status as any,
    itemInfo,
    Boolean(hasEditPermission),
    isVersionsEnabled,
  );

  const canSendToSign = actionItems.some((item: any) => item.action === "SEND");
  const canRejectSign = actionItems.some((item: any) => item.action === "RECALL");
  const canRollback = actionItems.some((item: any) => item.action === "RETURN");
  const canDelete = actionItems.some((item: any) => item.action === "DELETE");
  const canEdit = actionItems.some((item: any) => item.action === "EDIT");

  // Автоматическое обновление высоты iframe в unified режиме
  const wrapperRef = useRef<HTMLDivElement>(null);
  useUnifiedAutoResize(wrapperRef as RefObject<HTMLElement>);

  const openReject = () => {
    setRejectComment("");
    setRejectCommentError("");
    setRejectModalOpen(true);
  };

  const handleCloseRejectModal = () => {
    setRejectModalOpen(false);
    setRejectComment("");
    setRejectCommentError("");
  };

  const handleRejectComment = (comment: string) => {
    setRejectComment(comment);
  };

  const handleRejectSubmit = async (setError: (error: string) => void) => {
    if (!rejectComment.trim()) {
      setError("Укажите причину отклонения");
      return;
    }

    await handleReject({
      itemNoVersionId: itemNoVersionId || "",
      comment: rejectComment,
      signerEmployeeId: localUser?.user_id || "",
    });

    invalidateItem();
    handleCloseRejectModal();
  };

  const hasRealVersions =
    isVersionsEnabled &&
    !isVersionsForbidden &&
    (versionsData?.section3List?.length ?? 0) > 0;

  const titleVersionSwitcher =
    hasRealVersions && versions.length > 1 && itemInfo ? (
      <VersionSwitcherDropdown
        versions={versions}
        selectedVersionId={selectedVersionId}
        onVersionSelect={(v) => setSelectedVersionNumber(v.versionNumber)}
      />
    ) : null;

  return (
    <JournalRecordPageShell
      ref={wrapperRef}
      onClose={handleClose}
      historical={isViewingHistoricalVersion}
    >
        <Spin spinning={isFetching || isPending}>
          <div className="journal-info-header journal-info-header--info-page">
            <div className="journal-section-info-page__title-row">
              <div className="journal-section-info-page__title-with-switcher">
                <Title level={3} style={{ marginBottom: 0 }}>
                  Раздел № 3. Просмотр записи номер №{" "}
                  {displayItemInfo?.sequenceNumber ?? itemInfo?.sequenceNumber}
                </Title>
                {titleVersionSwitcher}
              </div>
            </div>

            <JournalRecordInfo
              updateDate={displayItemInfo?.updateDate}
              updateUserId={displayItemInfo?.updateUserId}
              status={displayItemInfo?.status}
              statusUpdateDate={
                recordVersionDate || displayItemInfo?.updateDate
              }
              currentVersionId={displayItemInfo?.currentVersionId}
              currentVersionNumber={hasVersion ? recordVersionNumber : undefined}
              signerInfo={displayItemInfo?.signer}
              onOpenAllVersions={
                isVersionsEnabled && hasVersion && itemNoVersionId
                  ? () => setVersionsListOpen(true)
                  : undefined
              }
            />
          </div>

          <div>
            <SectionThirdDetails
              workType={displayItemInfo?.workType}
              workDate={displayItemInfo?.workDate}
              workName={displayItemInfo?.workName}
              locationDescription={displayItemInfo?.locationDescription}
              workCondition={displayItemInfo?.workCondition}
              companyList={displayItemInfo?.companyList as any}
            />

            <SectionThirdDocuments docs={displayItemInfo?.docs} />

            <SectionExecutionData data={executionDataResponse?.entityList} />

            <div className="journal-info-block--spaced-bottom">
              <JournalSigner
                title="Подписант"
                signerLabel="Уполномоченный представитель лица, осуществляющего строительство"
                currentItem={displayItemInfo ?? itemInfo}
                signerFio={signer}
                isSignerAwaiting={isSignerAwaiting}
                sectionListId={sectionThreeListId}
                itemNoVersionId={itemNoVersionId || ""}
                localUser={localUser}
                signingString={signingString}
                isSigningPending={isSigningPending}
                getSigningXml={getSigningXml}
                setSigningString={setSigningString}
                withSignedString={withSignedString}
                invalidateItem={invalidateItem}
                openReject={openReject}
                canAction="section_3"
              />
            </div>
            <SectionThirdActions
              canSendToSign={
                isViewingHistoricalVersion ? false : canSendToSign
              }
              canRejectSign={
                isViewingHistoricalVersion ? false : canRejectSign
              }
              canRollback={isViewingHistoricalVersion ? false : canRollback}
              canDelete={isViewingHistoricalVersion ? false : canDelete}
              canEdit={isViewingHistoricalVersion ? false : canEdit}
              onOpenVersionsList={
                isVersionsEnabled && hasVersion && itemNoVersionId
                  ? () => setVersionsListOpen(true)
                  : undefined
              }
              itemNoVersionId={itemNoVersionId}
              handleIsForm={handleIsForm}
              navigate={navigate}
              projectId={projectId}
              objectId={objectId}
              mainJournalId={mainJournalId}
              titleId={titleId}
              handleSendToSign={handleSendToSign}
              handleStatusReturn={handleStatusReturn}
              handleReturn={handleReturn}
              deleteSectionEntry={deleteSectionEntry}
              invalidateItem={invalidateItem}
            />
          </div>
        </Spin>
      {isVersionsEnabled ? (
        <JournalVersionModal
          open={versionsListOpen}
          onCancel={() => setVersionsListOpen(false)}
          sectionThreeListId={
            journalData?.mainJournal?.section3?.sectionListId
          }
          itemNoVersionId={itemNoVersionId}
          sequenceNumber={
            displayItemInfo?.sequenceNumber ?? itemInfo?.sequenceNumber
          }
        />
      ) : null}
      <RejectModal
        open={rejectModalOpen}
        rejectComment={rejectComment}
        rejectCommentError={rejectCommentError}
        sectionThreeListId={sectionThreeListId}
        debouncedSearch=""
        page={1}
        limit={10}
        selectedStatuses={[]}
        dateStart=""
        dateEnd=""
        workTypeFilterId=""
        idFilterId=""
        signerFilterId=""
        createDate=""
        updateDate=""
        onCancel={handleCloseRejectModal}
        onCommentChange={handleRejectComment}
        onReject={handleRejectSubmit}
      />
    </JournalRecordPageShell>
  );
};

